'use client';

import { useEffect } from 'react';
import { useConsent } from '@/lib/consent';

/**
 * First-touch attribution tracker.
 *
 * On the visitor's first page of a session, this captures HOW they arrived —
 * landing page, referrer, UTM tags, and paid click IDs — into a first-party
 * cookie. The cookie is same-origin, so it rides along automatically on the POST
 * to /api/hubspot/contact, letting the server stamp every lead with its true source.
 *
 * This is the fix for the "OFFLINE source / zero attribution" gap that made the
 * SEO loop unable to tell which pages and keywords actually produce leads. It sets
 * the cookie once (first touch) and never overwrites it, so a visitor who lands on
 * /services/managed-it and later submits from /contact is still attributed to the
 * page that actually won them.
 */
const COOKIE = 'ip4_attribution';
const NINETY_DAYS = 60 * 60 * 24 * 90;

function hasCookie(name: string): boolean {
  if (typeof document === 'undefined') return false;
  return new RegExp('(?:^|;)' + name + '=').test(document.cookie);
}

export default function AttributionTracker() {
  const consent = useConsent();

  useEffect(() => {
    // Not strictly necessary, so only written after the visitor accepts cookies.
    if (consent !== 'granted') return;
    try {
      // First touch only — never overwrite an existing capture.
      if (hasCookie(COOKIE)) return;

      const params = new URLSearchParams(window.location.search);
      const attribution = {
        landing_page: window.location.pathname,
        referrer: document.referrer || '',
        utm_source: params.get('utm_source') || '',
        utm_medium: params.get('utm_medium') || '',
        utm_campaign: params.get('utm_campaign') || '',
        utm_term: params.get('utm_term') || '',
        utm_content: params.get('utm_content') || '',
        gclid: params.get('gclid') || '',      // Google Ads
        msclkid: params.get('msclkid') || '',  // Microsoft / Bing Ads
        fbclid: params.get('fbclid') || '',    // Meta / Facebook Ads
        captured_at: new Date().toISOString(),
      };

      const value = encodeURIComponent(JSON.stringify(attribution));
      document.cookie = `${COOKIE}=${value}; path=/; max-age=${NINETY_DAYS}; SameSite=Lax`;
    } catch {
      // Attribution capture must never break the page.
    }
  }, [consent]);

  return null;
}
